import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BasicCli from '../../main/components/basicCli/BasicCli';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import IGridItems from '../../main/interfaces/IGridItems';
import IPagination from '../../main/interfaces/IPagination';
import IExpenseCategory from '../../main/interfaces/IExpenseCategory';
import './Expenses.css';

type ExpenseCategoriesProps = {
  embedded?: boolean;
  onChanged?: () => void;
};

const emptyCategory: IExpenseCategory = {
  id: 0,
  name: '',
  description: '',
  isActive: true,
};

const ExpenseCategories = ({ embedded = false, onChanged }: ExpenseCategoriesProps) => {
  const { i18n } = useTranslation();
  const [rows, setRows] = useState<IExpenseCategory[]>([]);
  const [selected, setSelected] = useState<IExpenseCategory | null>(null);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<IExpenseCategory>(emptyCategory);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IExpenseCategory>({ defaultValues: emptyCategory });

  const fetchRows = (pgOverride?: IPagination, gridOverride?: IGridItems[]) => {
    const pg = pgOverride ?? pagination;
    axiosInstance
      .post(ApiConstants.expenses.categories.listct, {
        gridItems: gridOverride ?? gridItems,
        pagination: pg,
      })
      .then((res) => {
        setRows(res.data.items ?? []);
        setGridItems(res.data.gridItems ?? []);
        setPagination({ ...pg, count: res.data.pagination?.count ?? pg.count });
      })
      .catch(() => toast.error(i18n.t('errors.somethingWentWrong')));
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (item: IExpenseCategory) => {
    setEditingId(Number(item.id));
    setEditValues({
      id: item.id,
      name: item.name ?? '',
      description: item.description ?? '',
      isActive: item.isActive ?? true,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValues(emptyCategory);
  };

  const saveCreate = (data: IExpenseCategory) => {
    axiosInstance
      .post(ApiConstants.expenses.categories.save, {
        id: 0,
        name: data.name,
        description: data.description,
        isActive: data.isActive ?? true,
      })
      .then(() => {
        toast.success(i18n.t('expenses.categoryCreated'));
        reset(emptyCategory);
        setSelected(null);
        fetchRows();
        onChanged?.();
      })
      .catch((error) => toast.error(error?.response?.data?.message ?? i18n.t('errors.somethingWentWrong')));
  };

  const saveEdit = () => {
    if (!editingId) return;

    axiosInstance
      .post(ApiConstants.expenses.categories.save, {
        id: editingId,
        name: editValues.name,
        description: editValues.description,
        isActive: editValues.isActive ?? true,
      })
      .then(() => {
        toast.success(i18n.t('expenses.categoryUpdated'));
        cancelEdit();
        fetchRows();
        onChanged?.();
      })
      .catch((error) => toast.error(error?.response?.data?.message ?? i18n.t('errors.somethingWentWrong')));
  };

  const deleteItem = () => {
    if (!selected?.id) return;

    axiosInstance
      .delete(ApiConstants.expenses.categories.delete(selected.id))
      .then(() => {
        toast.success(i18n.t('expenses.categoryDeleted'));
        setIsDeleteOpen(false);
        setSelected(null);
        if (editingId === selected.id) cancelEdit();
        fetchRows();
        onChanged?.();
      })
      .catch((error) => toast.error(error?.response?.data?.message ?? i18n.t('errors.somethingWentWrong')));
  };

  const content = (
    <div className={`expensePage ${embedded ? 'isEmbedded' : ''}`}>
      <div className="expenseContainer">
          <div className="bcPageHeader expenseHeader">
            <div>
              <h1 className="bcPageTitle">{i18n.t('expenses.categoriesTitle')}</h1>
              {/* <p className="expenseSubtitle">{i18n.t('expenses.categoriesSubtitle')}</p> */}
            </div>
          </div>

          <div className="expenseCategoryManager">
            <section className="expenseCategorySection">
              {/* <h2>{i18n.t('expenses.addCategory')}</h2> */}
              <form className="expenseCategoryCreate" onSubmit={handleSubmit(saveCreate)}>
                <label className="expenseField">
                  <span>{i18n.t('generic.name')}</span>
                  <input className="expenseInput" type="text" placeholder={i18n.t('generic.name')} {...register('name', { required: true })} />
                  {errors?.name && <small className="expenseFieldError">{i18n.t('validation.required', { defaultValue: 'Required' })}</small>}
                </label>

                <label className="expenseField">
                  <span>{i18n.t('generic.description')}</span>
                  <input className="expenseInput" type="text" placeholder={i18n.t('generic.description')} {...register('description')} />
                </label>

                <label className="expenseCategoryInlineCheck">
                  <span>{i18n.t('entity.Active')}</span>
                  <input className="ui-check" type="checkbox" {...register('isActive')} />
                </label>

                <button type="submit" className="ui-control ui-btn ui-btn--primary expenseCategoryAddBtn" title={i18n.t('expenses.addCategory')}>
                  +
                </button>
              </form>
            </section>

            <section className="expenseCategorySection">
              <h2>{i18n.t('expenses.categoriesList', { defaultValue: 'Category List' })}</h2>
              <div className="expenseCategoryList">
                {rows.length === 0 ? (
                  <div className="expenseCategoryEmpty">{i18n.t('generic.noDataAvailable', { defaultValue: 'No data available' })}</div>
                ) : (
                  rows.map((item) => (
                    <div key={item.id} className="expenseCategoryRow">
                      {editingId === item.id ? (
                        <>
                          <label className="expenseField">
                            <span>{i18n.t('generic.name')}</span>
                            <input
                              className="expenseInput"
                              type="text"
                              value={editValues.name}
                              onChange={(e) => setEditValues((prev) => ({ ...prev, name: e.target.value }))}
                            />
                          </label>
                          <label className="expenseField">
                            <span>{i18n.t('generic.description')}</span>
                            <input
                              className="expenseInput"
                              type="text"
                              value={editValues.description ?? ''}
                              onChange={(e) => setEditValues((prev) => ({ ...prev, description: e.target.value }))}
                            />
                          </label>
                          <label className="expenseCategoryInlineCheck">
                            <span>{i18n.t('entity.Active')}</span>
                            <input
                              className="ui-check"
                              type="checkbox"
                              checked={!!editValues.isActive}
                              onChange={(e) => setEditValues((prev) => ({ ...prev, isActive: e.target.checked }))}
                            />
                          </label>
                        </>
                      ) : (
                        <>
                          <div className="expenseCategoryCell">
                            <span>{i18n.t('generic.name')}</span>
                            <strong>{item.name}</strong>
                          </div>
                          <div className="expenseCategoryCell">
                            <span>{i18n.t('generic.description')}</span>
                            <strong>{item.description || '-'}</strong>
                          </div>
                          <div className="expenseCategoryCell">
                            <span>{i18n.t('entity.Active')}</span>
                            <strong>{item.isActive ? '✓' : '-'}</strong>
                          </div>
                        </>
                      )}

                      <div className="expenseCategoryActions">
                        {editingId === item.id ? (
                          <>
                            <button type="button" className="expenseCategoryIconBtn isSave" onClick={saveEdit} title={i18n.t('actions.save')}>
                              <SaveIcon />
                            </button>
                            <button type="button" className="expenseCategoryIconBtn" onClick={cancelEdit} title={i18n.t('actions.cancel')}>
                              <CloseIcon />
                            </button>
                          </>
                        ) : (
                          <button type="button" className="expenseCategoryIconBtn" onClick={() => openEdit(item)} title={i18n.t('actions.edit')}>
                            <EditIcon />
                          </button>
                        )}
                        <button
                          type="button"
                          className="expenseCategoryIconBtn isDanger"
                          onClick={() => {
                            setSelected(item);
                            setIsDeleteOpen(true);
                          }}
                          title={i18n.t('actions.delete')}
                        >
                          <DeleteForeverIcon />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>

        {isDeleteOpen && selected && (
          <DeleteModal width="500px" handleClose={() => setIsDeleteOpen(false)} handleDelete={deleteItem}>
            {i18n.t('expenses.deleteCategoryConfirm', { name: selected.name })}
          </DeleteModal>
        )}
      </div>
  );

  if (embedded) return content;

  return <BasicCli>{content}</BasicCli>;
};

export default ExpenseCategories;
